'use client'

/**
 * Full-bleed gradient channel card for the marketplace's "Build your stack"
 * 3-col grid. Distinct from ChannelCard.tsx (which is the small fixed-width
 * card the original Rail layout used). Each card:
 *   - channel-gradient background fills the whole tile
 *   - icon tile top-left (semi-transparent white)
 *   - arrow top-right (semi-transparent white)
 *   - "N LIVE" or "AVAILABLE" mono eyebrow
 *   - channel name h3
 *   - "X of Y connected" sub
 * Clicks open the marketplace channel overlay; the overlay links onward to
 * /channels/[key] for the full detail page.
 */

import { HugeiconsIcon } from '@hugeicons/react'
import { ArrowUpRight01Icon } from '@hugeicons-pro/core-stroke-rounded'
import type { CatalogChannelView } from '@/lib/connectors/get-catalog'
import { cn } from '@/lib/utils'
import { channelGradientFor, channelIconFor } from './channel-visuals'

export function ChannelGridCard({
  channel,
  onOpen,
}: {
  channel: CatalogChannelView
  onOpen: (channelKey: string) => void
}) {
  const total = channel.subchannels.length
  const live = channel.subchannels.filter(
    (s) =>
      s.connection?.status === 'connected' || s.connection?.status === 'pending',
  ).length
  const icon = channelIconFor(channel.key)
  const gradient = channelGradientFor(channel.key)
  const isLive = live > 0
  const eyebrow = isLive ? `${live} LIVE` : 'AVAILABLE'

  return (
    <button
      type="button"
      onClick={() => onOpen(channel.key)}
      className={cn(
        'group relative flex min-h-48 flex-col justify-between overflow-hidden rounded-[18px] p-5 text-left text-white transition-transform duration-200 ease-out hover:-translate-y-[2px] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70',
        gradient,
      )}
    >
      <div className="flex items-start justify-between">
        <span className="grid size-11 place-items-center rounded-[12px] bg-white/25">
          <HugeiconsIcon icon={icon} className="size-5 text-white" aria-hidden />
        </span>
        <span className="grid size-7 place-items-center rounded-full bg-white/25">
          <HugeiconsIcon icon={ArrowUpRight01Icon} className="size-3.5 text-white" aria-hidden />
        </span>
      </div>

      <div>
        <div className="mb-1 font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-white/90">
          {eyebrow}
        </div>
        <div className="text-[22px] font-bold tracking-[-0.015em] leading-tight">
          {channel.name}
        </div>
        <div className="mt-1 font-mono text-xs text-white/85">
          {live} of {total} connected
        </div>
      </div>
    </button>
  )
}
